import type { ParseKeys } from "i18next";
import type { StatusTone } from "../components/ui";
import type { CommissionStatus, PayoutStatus, ReferralStatus } from "./types";

/*
 * What each state is called on screen, and which of the four colours it takes.
 *
 * One file rather than a ternary in each table, because the same commission
 * appears on three screens and a status that reads "Cleared" on one and
 * "Payable" on another is two features to somebody being paid by it.
 *
 * The colour rule is the workspace's: `mint` is done and good, `warn` is on its
 * way to trouble, `danger` has failed, neutral is everything deliberately not
 * moving. Nothing decorative may borrow one, which is why "waiting" is neutral
 * rather than amber — a hold window running normally is not trouble.
 */

export type StateKey = ParseKeys<"states">;

export interface StateLabel {
  label: StateKey;
  tone: StatusTone;
  /** The one sentence a reader needs when the word is not self-explanatory. */
  hint?: StateKey;
}

export const COMMISSION_STATES: Record<CommissionStatus, StateLabel> = {
  /*
   * Not `warn`, deliberately. A commission inside its refund window is the
   * normal path every commission takes, and painting the ordinary case amber
   * would spend the colour that means "this needs your attention" on the state
   * most rows are in most of the time.
   */
  pending: {
    label: "commission.pending.label",
    tone: "neutral",
    hint: "commission.pending.hint",
  },
  payable: { label: "commission.payable.label", tone: "live", hint: "commission.payable.hint" },
  paid: { label: "commission.paid.label", tone: "live" },
  /*
   * `danger`, and it earns it: this is money that was earned and then taken
   * back because the customer's payment was refunded or charged back. It is the
   * one state on this dashboard somebody has to be told about rather than
   * merely shown, which is why the row also carries `resolutionReason`.
   */
  reversed: { label: "commission.reversed.label", tone: "danger" },
  void: { label: "commission.void.label", tone: "neutral" },
};

export const REFERRAL_STATES: Record<ReferralStatus, StateLabel> = {
  signed_up: {
    label: "referral.signed_up.label",
    tone: "neutral",
    hint: "referral.signed_up.hint",
  },
  converted: { label: "referral.converted.label", tone: "live" },
  /*
   * Shown rather than hidden. A rejected referral is one this program refused
   * to credit — a self-referral, or one that arrived while the program was
   * closed — and an affiliate who sees a signup vanish with no explanation
   * assumes the tracking is broken.
   */
  rejected: { label: "referral.rejected.label", tone: "neutral" },
};

export const PAYOUT_STATES: Record<PayoutStatus, StateLabel> = {
  requested: { label: "payout.requested.label", tone: "neutral", hint: "payout.requested.hint" },
  approved: { label: "payout.approved.label", tone: "neutral", hint: "payout.approved.hint" },
  processing: { label: "payout.processing.label", tone: "neutral" },
  paid: { label: "payout.paid.label", tone: "live" },
  failed: { label: "payout.failed.label", tone: "danger" },
  cancelled: { label: "payout.cancelled.label", tone: "neutral" },
  /*
   * `warn`, not `danger`, and the distinction is the whole reason this is a
   * separate state from `cancelled`.
   *
   * `danger` on this dashboard means money that was earned and has gone —
   * a reversed commission, a failed send. A refused payout is neither: the
   * commissions went straight back to cleared and can be requested again. What
   * it is is something the affiliate has to act on before that will work, which
   * is exactly what `warn` reserves itself for. The reason an operator wrote is
   * shown beside it, because a refusal with no explanation is a support ticket.
   */
  rejected: { label: "payout.rejected.label", tone: "warn" },
};

/**
 * Whether a payout can still be called off.
 *
 * `requested` only, matching the API — past that it is in the send queue and
 * may already have been sent, and a cancel that raced that transfer would leave the
 * money gone and the commissions released to be claimed a second time. The
 * button is hidden rather than disabled for every other state, because a
 * disabled control invites somebody to work out how to enable it.
 */
export function canCancel(status: PayoutStatus): boolean {
  return status === "requested";
}
